import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { 
  FiMonitor, 
  FiSmartphone, 
  FiTablet, 
  FiMapPin, 
  FiClock, 
  FiKey, 
  FiShield, 
  FiAlertTriangle,
  FiLogOut,
  FiActivity,
  FiEdit2,
  FiCheck,
  FiX,
  FiRefreshCw
} from "react-icons/fi";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";

const BaseUrl = process.env.REACT_APP_API_URL;

const LoginActivity = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmOthersModal, setShowConfirmOthersModal] = useState(false);
  const [showConfirmSingleModal, setShowConfirmSingleModal] = useState(null); // stores sessionId to revoke
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [newDeviceName, setNewDeviceName] = useState("");

  const loginDataStr = Cookies.get("loginData");
  const parsedData = loginDataStr ? JSON.parse(loginDataStr) : null;
  const userId = parsedData?.data?._id;
  const currentSessionId = parsedData?.sessionId;

  const fetchData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const activeRes = await axios.get(`${BaseUrl}/Session/active`, {
        params: { userId },
        headers: { "user-id": userId }
      });
      const historyRes = await axios.get(`${BaseUrl}/Session/history`, {
        params: { userId },
        headers: { "user-id": userId }
      });

      if (activeRes.data?.status === "Success") {
        setActiveSessions(activeRes.data.data);
      }
      if (historyRes.data?.status === "Success") {
        setLoginHistory(historyRes.data.data);
      }
    } catch (err) {
      console.error("Error fetching login activity:", err);
      toast.error("Failed to load active sessions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleRenameSession = async (sessionId) => {
    if (!newDeviceName.trim()) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${BaseUrl}/Session/rename/${sessionId}`, {
        deviceName: newDeviceName.trim()
      });
      if (res.data?.status === "Success") {
        toast.success("Device renamed successfully.");
        if (sessionId === currentSessionId) {
          localStorage.setItem("custom_device_name", newDeviceName.trim());
        }
        setEditingSessionId(null);
        setNewDeviceName("");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to rename device.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeSingle = async (sessionId) => {
    setActionLoading(true);
    const thisSessionId = currentDeviceSession?._id || storedSessionId;
    try {
      const res = await axios.post(`${BaseUrl}/Session/revoke/${sessionId}`);
      if (res.data?.status === "Success") {
        toast.success("Session logged out successfully.");
        if (sessionId === thisSessionId) {
          Cookies.remove("loginData");
          Cookies.remove("active_session_id");
          localStorage.removeItem("active_session_id");
          window.location.href = "/";
        } else {
          fetchData();
        }
      }
    } catch (err) {
      toast.error("Failed to revoke session.");
    } finally {
      setActionLoading(false);
      setShowConfirmSingleModal(null);
    }
  };

  const handleRevokeOthers = async () => {
    setActionLoading(true);
    const thisSessionId = currentDeviceSession?._id || storedSessionId;
    try {
      const res = await axios.post(`${BaseUrl}/Session/revoke-others`, {
        userId,
        currentSessionId: thisSessionId
      }, {
        headers: { "user-id": userId }
      });
      if (res.data?.status === "Success") {
        toast.success("Successfully logged out all other sessions.");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to revoke other sessions.");
    } finally {
      setActionLoading(false);
      setShowConfirmOthersModal(false);
    }
  };

  const getDeviceIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "mobile":
        return <FiSmartphone className="w-5 h-5 text-indigo-600" />;
      case "tablet":
        return <FiTablet className="w-5 h-5 text-purple-600" />;
      default:
        return <FiMonitor className="w-5 h-5 text-blue-600" />;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata"
    });
  };

  // Columns for Login History DynamicTable
  const historyColumns = useMemo(() => [
    { field: "no", headerName: "No", width: 60 },
    {
      field: "action",
      headerName: "Event",
      width: 120,
      renderCell: (params) => {
        const action = params.row.raw.action;
        return (
          <span
            className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
              action === "login"
                ? "bg-green-100 text-green-800 border border-green-200"
                : action === "logout"
                ? "bg-gray-100 text-gray-700 border border-gray-200"
                : "bg-amber-100 text-amber-800 border border-amber-200"
            }`}
          >
            {action}
          </span>
        );
      }
    },
    {
      field: "device",
      headerName: "Device / Hostname",
      flex: 1.5,
      renderCell: (params) => (
        <span className="font-bold text-gray-800 text-xs">
          {params.row.raw.deviceName || "Windows PC"}
        </span>
      )
    },
    {
      field: "osBrowser",
      headerName: "OS & Browser",
      flex: 1.2,
      renderCell: (params) => (
        <span className="text-xs text-gray-700 font-medium">
          {params.row.raw.os || "Windows"} • {params.row.raw.browser || "Chrome"}
        </span>
      )
    },
    {
      field: "locationIp",
      headerName: "Location & IP",
      flex: 1.5,
      renderCell: (params) => (
        <div className="text-xs text-gray-600 leading-tight">
          <div className="font-medium text-gray-800">{params.row.raw.location || "Unknown"}</div>
          <div className="text-[11px] text-gray-400 font-mono">IP: {params.row.raw.ipAddress || "Unknown"}</div>
        </div>
      )
    },
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 1.3,
      renderCell: (params) => (
        <span className="text-xs text-gray-700">
          {formatDateTime(params.row.raw.timestamp)}
        </span>
      )
    }
  ], []);

  const historyRows = useMemo(() => 
    loginHistory.map((log, index) => ({
      id: log._id,
      no: index + 1,
      raw: log
    })),
    [loginHistory]
  );

  const isMatchingThisBrowser = (session) => {
    const ua = navigator.userAgent;
    let browserMatch = false;
    if (/Chrome|CriOS/.test(ua) && session.browser === 'Chrome') browserMatch = true;
    else if (/Safari/.test(ua) && !/Chrome/.test(ua) && session.browser === 'Safari') browserMatch = true;
    else if (/Firefox|FxiOS/.test(ua) && session.browser === 'Firefox') browserMatch = true;
    else if (/Edg|EdgiOS/.test(ua) && session.browser === 'Edge') browserMatch = true;
    else if (/Opera|OPR|OPiOS/.test(ua) && session.browser === 'Opera') browserMatch = true;

    let osMatch = false;
    if (/Windows/.test(ua) && /Windows/.test(session.os)) osMatch = true;
    else if (/Macintosh/.test(ua) && /macOS/.test(session.os)) osMatch = true;
    else if (/iPhone|iPad/.test(ua) && /iOS/.test(session.os)) osMatch = true;
    else if (/Android/.test(ua) && /Android/.test(session.os)) osMatch = true;

    return browserMatch && osMatch;
  };

  const storedSessionId = 
    parsedData?.sessionId || 
    Cookies.get("active_session_id") || 
    localStorage.getItem("active_session_id");

  let currentDeviceSession = null;
  if (storedSessionId) {
    currentDeviceSession = activeSessions.find(s => s._id === storedSessionId);
  }
  if (!currentDeviceSession && activeSessions.length > 0) {
    currentDeviceSession = activeSessions.find(isMatchingThisBrowser);
  }
  if (!currentDeviceSession && activeSessions.length > 0) {
    currentDeviceSession = activeSessions[activeSessions.length - 1];
  }

  useEffect(() => {
    if (currentDeviceSession?._id && (!storedSessionId || storedSessionId !== currentDeviceSession._id)) {
      localStorage.setItem("active_session_id", currentDeviceSession._id);
      Cookies.set("active_session_id", currentDeviceSession._id, { expires: 7 });
    }
  }, [currentDeviceSession, storedSessionId]);

  const otherSessions = activeSessions.filter(s => s._id !== currentDeviceSession?._id);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6 font-sans">
      {/* Clean Minimal Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
              <FiShield size={18} />
            </div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Login Activity</h1>
          </div>
          <p className="text-xs text-slate-500">Manage and secure your active login sessions across devices.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            title="Refresh sessions"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors border-none cursor-pointer"
          >
            <FiRefreshCw size={13} />
            Refresh
          </button>

          {otherSessions.length > 0 && (
            <button
              onClick={() => setShowConfirmOthersModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-lg transition-all border-none cursor-pointer shadow-sm"
            >
              <FiLogOut size={13} />
              Log Out All Other Devices ({otherSessions.length})
            </button>
          )}
        </div>
      </div>

      {/* Top Section: Active Sessions */}
      <div className="space-y-6">
        {/* 1. Current Device Card */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <FiActivity className="text-emerald-600" />
              Current Device (This Device)
            </h2>
            <span className="text-[10px] font-extrabold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200 uppercase flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Active Now
            </span>
          </div>

          {currentDeviceSession && (
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-5 bg-gradient-to-r from-emerald-50/40 via-white to-white border border-emerald-200 rounded-2xl shadow-sm gap-4">
              <div className="flex items-start gap-4 w-full sm:w-auto">
                <div className="p-3 rounded-2xl border bg-emerald-600/10 border-emerald-200 text-emerald-600 shrink-0">
                  {getDeviceIcon(currentDeviceSession.deviceType)}
                </div>

                <div className="space-y-1 flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    {editingSessionId === currentDeviceSession._id ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleRenameSession(currentDeviceSession._id);
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <input
                          type="text"
                          value={newDeviceName}
                          onChange={(e) => setNewDeviceName(e.target.value)}
                          placeholder="e.g. DSPL-22 or Office PC..."
                          className="px-2.5 py-1 text-xs font-semibold border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={actionLoading}
                          className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-[11px] font-bold rounded-lg border-none cursor-pointer hover:bg-indigo-700 transition-colors shadow-sm"
                        >
                          <FiCheck size={12} />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSessionId(null);
                            setNewDeviceName("");
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-[11px] font-bold rounded-lg border-none cursor-pointer hover:bg-slate-200 transition-colors"
                        >
                          <FiX size={12} />
                          Cancel
                        </button>
                      </form>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-sm sm:text-base text-slate-800 tracking-tight">
                          {currentDeviceSession.deviceName || "Windows PC"}
                        </span>
                        <button
                          onClick={() => {
                            setEditingSessionId(currentDeviceSession._id);
                            setNewDeviceName(currentDeviceSession.deviceName || "");
                          }}
                          title="Rename this device"
                          className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md border border-indigo-100 transition-all cursor-pointer"
                        >
                          <FiEdit2 size={10} />
                          <span>Rename</span>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                    <span>{currentDeviceSession.os || "Windows"}</span>
                    <span className="text-slate-300">•</span>
                    <span>{currentDeviceSession.browser || "Chrome"}</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-slate-500 pt-0.5">
                    <span className="flex items-center gap-1.5">
                      <FiMapPin className="text-slate-400 shrink-0" size={13} />
                      <span className="truncate">{currentDeviceSession.location || "Local Network"}</span>
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[11px]">
                      <span className="font-bold text-slate-400 shrink-0 font-sans">IP:</span>
                      {currentDeviceSession.ipAddress || "Unknown"}
                    </span>
                    <span className="flex items-center gap-1.5 col-span-1 sm:col-span-2">
                      <FiClock className="text-slate-400 shrink-0" size={13} />
                      <span>Last Active: <strong className="text-emerald-700">Just now</strong></span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="self-end sm:self-center shrink-0">
                <button
                  onClick={() => setShowConfirmSingleModal(currentDeviceSession._id)}
                  className="px-3.5 py-2 text-xs font-bold bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800 border border-slate-200 rounded-xl transition-all duration-200 cursor-pointer"
                >
                  Log Out
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 2. Other Active Devices Section */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <FiMonitor className="text-indigo-600" />
              Other Active Devices ({otherSessions.length})
            </h2>

            {otherSessions.length > 0 && (
              <button
                onClick={() => setShowConfirmOthersModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-lg transition-all border-none cursor-pointer shadow-sm"
              >
                <FiLogOut size={13} />
                Log Out All Other Devices
              </button>
            )}
          </div>

          {otherSessions.length > 0 ? (
            <div className="grid gap-3">
              {otherSessions.map((session) => (
                <div
                  key={session._id}
                  className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-2xl hover:shadow-sm transition-all duration-200 gap-4"
                >
                  <div className="flex items-start gap-4 w-full sm:w-auto">
                    <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 text-slate-600 shrink-0">
                      {getDeviceIcon(session.deviceType)}
                    </div>

                    <div className="space-y-1 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        {editingSessionId === session._id ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleRenameSession(session._id);
                            }}
                            className="flex items-center gap-1.5"
                          >
                            <input
                              type="text"
                              value={newDeviceName}
                              onChange={(e) => setNewDeviceName(e.target.value)}
                              placeholder="e.g. DSPL-22 or Office PC..."
                              className="px-2.5 py-1 text-xs font-semibold border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
                              autoFocus
                            />
                            <button
                              type="submit"
                              disabled={actionLoading}
                              className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-[11px] font-bold rounded-lg border-none cursor-pointer hover:bg-indigo-700 transition-colors shadow-sm"
                            >
                              <FiCheck size={12} />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingSessionId(null);
                                setNewDeviceName("");
                              }}
                              className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-[11px] font-bold rounded-lg border-none cursor-pointer hover:bg-slate-200 transition-colors"
                            >
                              <FiX size={12} />
                              Cancel
                            </button>
                          </form>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-sm sm:text-base text-slate-800 tracking-tight">
                              {session.deviceName || "Windows PC"}
                            </span>
                            <button
                              onClick={() => {
                                setEditingSessionId(session._id);
                                setNewDeviceName(session.deviceName || "");
                              }}
                              title="Rename this device"
                              className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md border border-indigo-100 transition-all cursor-pointer"
                            >
                              <FiEdit2 size={10} />
                              <span>Rename</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                        <span>{session.os || "Windows"}</span>
                        <span className="text-slate-300">•</span>
                        <span>{session.browser || "Chrome"}</span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-slate-500 pt-0.5">
                        <span className="flex items-center gap-1.5">
                          <FiMapPin className="text-slate-400 shrink-0" size={13} />
                          <span className="truncate">{session.location || "Local Network"}</span>
                        </span>
                        <span className="flex items-center gap-1.5 font-mono text-[11px]">
                          <span className="font-bold text-slate-400 shrink-0 font-sans">IP:</span>
                          {session.ipAddress || "Unknown"}
                        </span>
                        <span className="flex items-center gap-1.5 col-span-1 sm:col-span-2">
                          <FiClock className="text-slate-400 shrink-0" size={13} />
                          <span>Last Active: <strong>{formatDateTime(session.lastActiveAt)}</strong></span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="self-end sm:self-center shrink-0">
                    <button
                      onClick={() => setShowConfirmSingleModal(session._id)}
                      className="px-3.5 py-2 text-xs font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 border border-rose-100 rounded-xl transition-all duration-200 cursor-pointer flex items-center gap-1.5"
                    >
                      <FiLogOut size={13} />
                      Log Out
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl text-center text-xs text-slate-500">
              No other devices are currently logged in with your account.
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section: Login & Access History in Project's DynamicTable */}
      {historyRows.length > 0 && (
        <div className="space-y-3 pt-4 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 select-none">
              <FiKey className="text-emerald-600" />
              Login & Access History
            </h2>
            <span className="bg-[#112A47] text-white px-3 py-1 text-xs font-bold rounded shadow">
              Total Logs: {historyRows.length}
            </span>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <DynamicTable columns={historyColumns} rows={historyRows} searchable={false} height={350} />
          </div>
        </div>
      )}

      {/* Modal - Log Out Others */}
      {showConfirmOthersModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate__animated animate__fadeIn animate__faster">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-w-md w-full text-center space-y-5 animate__animated animate__zoomIn animate__faster">
            <div className="w-14 h-14 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto border border-rose-100">
              <FiAlertTriangle size={28} />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-lg font-bold text-gray-800">Log Out Other Sessions?</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                This will immediately terminate all active sessions on other computers or phones. You will remain logged in on this device.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                disabled={actionLoading}
                onClick={() => setShowConfirmOthersModal(false)}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 font-bold text-xs rounded-lg cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                disabled={actionLoading}
                onClick={handleRevokeOthers}
                className="flex-1 py-2.5 bg-rose-600 text-white hover:bg-rose-700 font-bold text-xs rounded-lg border-none cursor-pointer"
              >
                {actionLoading ? "Logging out..." : "Yes, Log Out Others"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Log Out Single */}
      {showConfirmSingleModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate__animated animate__fadeIn animate__faster">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-w-md w-full text-center space-y-5 animate__animated animate__zoomIn animate__faster">
            <div className="w-14 h-14 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto border border-amber-100">
              <FiAlertTriangle size={28} />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-lg font-bold text-gray-800">Log Out This Device?</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Are you sure you want to terminate this active login session? The selected device will be logged out immediately.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                disabled={actionLoading}
                onClick={() => setShowConfirmSingleModal(null)}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 font-bold text-xs rounded-lg cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                disabled={actionLoading}
                onClick={() => handleRevokeSingle(showConfirmSingleModal)}
                className="flex-1 py-2.5 bg-rose-600 text-white hover:bg-rose-700 font-bold text-xs rounded-lg border-none cursor-pointer"
              >
                {actionLoading ? "Logging out..." : "Log Out Device"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginActivity;
